/* eslint-disable react/prop-types */
import cx from "classnames";
import * as d3 from "d3";
import { Component } from "react";

import CS from "metabase/css/core/index.css";
import { formatValue } from "metabase/lib/formatting";
import { isSameSeries } from "metabase/visualizations/lib/utils";

const LegacyChoropleth = ({
  series,
  geoJson,
  projection,
  projectionFrame,
  getColor,
  onHoverFeature,
  onClickFeature,
  showLabelsRegion,
  showLabelsMetric,
  valuesMap,
  columnSettings,
  dimensionIndex,
  nameProperty,
  keyProperty,
}) => {
  const geo = d3.geoPath().projection(projection);

  const [[minX, minY], [maxX, maxY]] = projectionFrame.map(projection);
  const width = maxX - minX;
  const height = maxY - minY;

  return (
    <div
      className={cx(
        CS.absolute,
        CS.top,
        CS.bottom,
        CS.left,
        CS.right,
        CS.flex,
        CS.layoutCentered,
      )}
    >
      <ShouldUpdate
        series={series}
        shouldUpdate={(props, nextProps) =>
          !isSameSeries(props.series, nextProps.series)
        }
      >
        {() => (
          <svg
            className={cx(CS.flexFull, CS.m1)}
            viewBox={`${minX} ${minY} ${width} ${height}`}
          >
            {geoJson.features.map((feature, index) => (
              <path
                data-testid="choropleth-feature"
                key={index}
                d={geo(feature, index)}
                stroke="white"
                strokeWidth={1}
                fill={getColor(feature)}
                onMouseMove={(e) =>
                  onHoverFeature({
                    feature: feature,
                    event: e.nativeEvent,
                  })
                }
                onMouseLeave={() => onHoverFeature(null)}
                className={cx({ [CS.cursorPointer]: !!onClickFeature })}
                onClick={
                  onClickFeature
                    ? (e) =>
                        onClickFeature({
                          feature: feature,
                          event: e.nativeEvent,
                        })
                    : undefined
                }
              />
            ))}
            {(showLabelsRegion || showLabelsMetric) &&
              valuesMap &&
              columnSettings &&
              geoJson.features.map((feature, index) => {
                const bounds = geo.bounds(feature);
                const [x, y] = geo.centroid(feature);
                // Use the keyProperty to get the correct matching key for the feature
                const featureKey = keyProperty
                  ? String(feature.properties[keyProperty] || "").toLowerCase()
                  : String(
                      feature.properties[Object.keys(feature.properties)[0]] || "",
                    ).toLowerCase();
                const value = valuesMap[featureKey];

                if (value == null) {
                  return null;
                }

                const textElements = [];
                let offsetY = 0;

                if (showLabelsRegion) {
                  const featureName = String(
                    feature.properties[nameProperty] || "",
                  );
                  textElements.push(
                    <text
                      key="region"
                      x={x}
                      y={y + offsetY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="12"
                      fontWeight="700"
                      fill="#4c5773"
                      stroke="white"
                      strokeWidth="3"
                      paintOrder="stroke"
                      pointerEvents="none"
                    >
                      {featureName}
                    </text>
                  );
                  offsetY += 8;
                }

                if (showLabelsMetric) {
                  const formattedValue = formatValue(value, columnSettings);
                  offsetY = showLabelsRegion ? offsetY : offsetY - 8;
                  textElements.push(
                    <text
                      key="metric"
                      x={x}
                      y={y + offsetY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="12"
                      fontWeight="700"
                      fill="#4c5773"
                      stroke="white"
                      strokeWidth="3"
                      paintOrder="stroke"
                      pointerEvents="none"
                    >
                      {formattedValue}
                    </text>
                  );
                }

                return (
                  <g key={`label-${index}`}>
                    {textElements}
                  </g>
                );
              })}
          </svg>
        )}
      </ShouldUpdate>
    </div>
  );
};

class ShouldUpdate extends Component {
  shouldComponentUpdate(nextProps) {
    if (nextProps.shouldUpdate) {
      return nextProps.shouldUpdate(this.props, nextProps);
    }
    return true;
  }
  render() {
    const { children } = this.props;
    if (typeof children === "function") {
      return children();
    } else {
      return children;
    }
  }
}

export default LegacyChoropleth;
